// Google's tag, loaded and spoken to the way its snippet does, and only once
// analytics may run (the entry point decides when to call `startGtag`:
// consent granted, or implied where none is required). Consent Mode v2 in
// its basic form: the script is not on the page before that, and when it
// loads the tag still hears the default-denied call first, then the grant
// of `analytics_storage` alone (no ads here), so its own state is right.
// Withdrawing in Settings sends the matching denial (`updateConsent`), so a
// loaded tag stops too. Page views are manual (`send_page_view: false`): one
// per route, as a full URL of the reduced path.
import type { Analytics, AnalyticsValue } from '@chess/api-analytics';
import { sanitizeEvent } from './sanitize.ts';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const SCRIPT_SRC = 'https://www.googletagmanager.com/gtag/js';

export interface GtagOptions {
  /** `debug_mode` on the config, so events show in GA's DebugView. */
  debug?: boolean;
}

/** Tells a loaded tag whether `analytics_storage` is granted now; nothing happens when no tag was started. */
export function updateConsent(granted: boolean): void {
  window.gtag?.('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' });
}

/** Loads the tag for a stream and returns the live analytics over it. Loading twice is harmless: the script is appended once. */
export function startGtag(id: string, options: GtagOptions = {}): Analytics {
  const w = window;
  w.dataLayer = w.dataLayer ?? [];
  // The tag reads `arguments` objects off the data layer, as in Google's snippet; an array is not the same to it.
  function gtag(..._args: unknown[]) {
    w.dataLayer!.push(arguments);
  }
  w.gtag = gtag;
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  gtag('js', new Date());
  gtag('config', id, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...(options.debug ? { debug_mode: true } : {}),
  });
  gtag('consent', 'update', { analytics_storage: 'granted' });

  if (!document.querySelector(`script[src^="${SCRIPT_SRC}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `${SCRIPT_SRC}?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
  }

  return {
    page(path: string) {
      gtag('event', 'page_view', {
        page_path: path,
        page_location: `${location.origin}${path}`,
        page_title: document.title,
      });
    },
    track(event: string, params?: Record<string, AnalyticsValue>) {
      const clean = sanitizeEvent(event, params);
      if (clean) gtag('event', clean.name, clean.params);
    },
    enabled: () => true,
  };
}
